import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MultiSelect } from '@progress/kendo-react-dropdowns';
import { translate } from '@utils/i18n';
import useDebounce from '@hooks/useDebounce';
import { lookupPath } from 'routes';
import './ContactsSelectorStyles.scss';

const namespace = 'features.accounts.contacts_selector';
const TYPE = 'contact';
const PAGE_SIZE = 32;

function ContactsSelector() {
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [filterQuery, setFilterQuery] = useState('');
  const [opened, setOpened] = useState(false);
  const searchInputRef = useRef(null);
  const debouncedQuery = useDebounce(filterQuery.trim());

  useEffect(() => {
    const lookupParams = { type: TYPE, take: PAGE_SIZE, skip };

    if (debouncedQuery !== '') {
      lookupParams.filter = { filters: { 0: { field: 'name', operator: 'contains', value: debouncedQuery } } };
    }

    const controller = new AbortController();

    const loadContacts = async () => {
      try {
        const response = await fetch(lookupPath(lookupParams), { signal: controller.signal });
        const json = await response.json();

        setData(json.data);
        setTotalCount(json.total_count);
      } catch (error) {
        if (error.name === 'AbortError') return;

        setData([]);
        setTotalCount(0);
      }
    };

    loadContacts();

    return () => {
      controller.abort();
    };
  }, [skip, debouncedQuery]);

  useEffect(() => {
    if (opened && searchInputRef.current) searchInputRef.current.focus();
  }, [opened]);

  const handleChange = (event) => {
    setSelected(event.value);
  };

  const handlePageChange = (event) => {
    setSkip(event.page.skip);
  };

  const handleOpen = () => {
    setOpened(true);
  };

  const handleClose = () => {};

  const handleSearchChange = (event) => {
    setFilterQuery(event.target.value);
    setSkip(0);
  };

  useEffect(() => {
    if (!opened) return undefined;

    const handleOutsideClick = (event) => {
      const inField = event.target.closest('[data-react-class="Accounts/ContactsSelector"]');
      const inPopup = event.target.closest('.contacts-selector-popup');
      if (!inField && !inPopup) {
        setOpened(false);
        setFilterQuery('');
        setSkip(0);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [opened]);

  const selectedIds = useMemo(() => selected.map((item) => item.value), [selected]);

  const popupHeader = (
    <div
      className="contacts-selector__search k-searchbox"
      role="presentation"
      onMouseDown={(event) => event.preventDefault()}>
      <i className="fa-light fa-magnifying-glass contacts-selector__search-icon" />
      <input
        ref={searchInputRef}
        type="text"
        className="contacts-selector__search-input"
        value={filterQuery}
        placeholder={translate('placeholder', { namespace })}
        aria-label={translate('placeholder', { namespace })}
        onMouseDown={(event) => event.stopPropagation()}
        onChange={handleSearchChange}
      />
    </div>
  );

  return (
    <div className="contacts-selector">
      <MultiSelect
        textField="label"
        dataItemKey="value"
        data={data}
        value={selected}
        opened={opened}
        onOpen={handleOpen}
        onClose={handleClose}
        onChange={handleChange}
        onPageChange={handlePageChange}
        virtual={{ total: totalCount, pageSize: PAGE_SIZE, skip }}
        placeholder={translate('placeholder', { namespace })}
        header={popupHeader}
        popupSettings={{ className: 'contacts-selector-popup' }}
      />
      {selectedIds.map((id) => (
        <input key={id} type="hidden" name="contacts[]" value={id} />
      ))}
    </div>
  );
}

export default ContactsSelector;
